import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultMixer,
  type DirectorMessagePayload,
  type FrameBackground,
  type OverlayLayout,
  type SessionConfigPayload,
  type SoundChannel,
  type SoundCommand,
  type StageStatePayload,
  type ViewerEvent,
} from '@efira/protocol';

import { OverlayFrame } from './OverlayFrame';
import { SceneHost } from './scenes/SceneHost';
import { DirectorMessage } from './DirectorMessage';
import { QuotaNotice } from './QuotaNotice';
import { Watermark } from './Watermark';
import { createBus } from './realtime/bus';
import type { WidgetTotals } from './widgets/WidgetHost';
import { connectOverlay, tokenFromLocation, type ConnectionStatus } from './realtime/connection';

/**
 * Корень overlay.
 *
 * ГЛАВНОЕ ПРАВИЛО ЭТОГО ФАЙЛА: В ЭФИРЕ OVERLAY МОЛЧИТ.
 *
 * Overlay транслируется на аудиторию. Служебное сообщение в кадре - это текст
 * поверх лица стримера, который видят зрители и который стример заметит
 * последним: он смотрит в dashboard, а не в свой эфир глазами зрителя.
 * Поэтому неработающий overlay остаётся ПРОЗРАЧНЫМ.
 *
 * Диагностика показывается только при явном признаке: `?debug=1` в адресе или
 * режим разработки. Стример, которому нужно понять, что не так, добавляет
 * параметр - и видит всё; зритель не видит ничего никогда.
 *
 * Исключение, которое появится в блоке 6: исчерпание квоты требует ЯВНОГО
 * сообщения зрителю, а не молчания - иначе эфир просто гаснет без объяснения.
 * Место под него размечено ниже и намеренно не наполнено.
 */

const API_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3003';

function debugEnabled(): boolean {
  if (import.meta.env.DEV) {
    return true;
  }

  return new URLSearchParams(window.location.search).get('debug') === '1';
}

const TOKEN = tokenFromLocation(window.location.pathname);

/**
 * Начальный статус вычисляется сразу, а не в эффекте: отсутствие токена в
 * адресе - это свойство самого адреса, известное до всякого подключения.
 * Устанавливать его эффектом означало бы один лишний кадр «подключаемся» там,
 * где подключаться не к чему.
 */
const INITIAL_STATUS: ConnectionStatus =
  TOKEN === null
    ? {
        kind: 'rejected',
        reason: {
          reason: 'token_invalid',
          message: 'В адресе нет токена. Ожидается /s/<token>.',
          serverProtocol: { major: 0, minor: 0 },
        },
      }
    : { kind: 'connecting' };

/**
 * Пустая раскладка - стабильная ссылка.
 *
 * Литерал в месте использования пересоздавался бы на каждый рендер и
 * перезапускал бы эффект монтирования сцены, то есть перемонтировал бы игру
 * посреди эфира. Такие дефекты не видны ни в typecheck, ни в тестах.
 */
const EMPTY_LAYOUT: OverlayLayout = {};

/**
 * Виджеты - ЛЕНИВЫЙ ЧАНК.
 *
 * Оболочка overlay везёт то, что нужно всегда; виджеты нужны только тому,
 * кто их завёл. Ворота бюджета считают граф точки входа, и этот код в него
 * не входит.
 */
const WidgetHost = lazy(() => import('./widgets/WidgetHost'));

/**
 * Звук - ТОЖЕ ЛЕНИВЫЙ ЧАНК и тоже вне бюджета оболочки.
 *
 * Грузится только когда пришла первая команда: у стримера без падов звука
 * нет вовсе, и платить за него первой загрузкой он не должен.
 */
const SoundHost = lazy(() => import('./sound/SoundHost'));

/**
 * Сцена платформы - тоже ленивый чанк.
 *
 * Озвучка, оповещения, эффекты подарков, гифка и музыка нужны не всякому
 * стримеру и не с первой секунды. Код грузится первой командой сцены.
 */
const StageHost = lazy(() => import('./stage/StageHost'));

/** Настройки эфира до прихода конфигурации: ничего не включено. */
const NO_STREAM = {
  reader: false,
  alerts: false,
  giftFx: false,
  voice: null,
  readShoutouts: false,
  seconds: 10,
};

const EMPTY_TOTALS: WidgetTotals = {
  followers: 0,
  likes: 0,
  coins: 0,
  shares: 0,
  gifters: {},
  likers: {},
  names: {},
  secondsLeft: null,
};

/**
 * Прозрачный фон - состояние по умолчанию и до прихода контракта.
 *
 * Стабильная ссылка по той же причине, что и у пустой раскладки: литерал в
 * месте использования перезапускал бы эффекты каждым рендером.
 */
const TRANSPARENT: FrameBackground = { transparent: true, color: '#000000', image: '' };

export function App() {
  const [status, setStatus] = useState<ConnectionStatus>(INITIAL_STATUS);
  const [config, setConfig] = useState<SessionConfigPayload | null>(null);
  // Счётчики потока - только для диагностики: лента событий появится в Фазе 4
  // вместе с играми. Здесь важно видеть, что поток дошёл.
  const [counters, setCounters] = useState({ events: 0, members: 0, actions: 0, chat: 0 });
  // Последняя команда - для диагностики: видно не только что поток дошёл, но и
  // что привязка сработала именно та, которая ожидалась.
  const [lastAction, setLastAction] = useState<string | null>(null);
  /** Последнее сообщение ведущего: снимается само по своей длительности. */
  const [directorMessage, setDirectorMessage] = useState<DirectorMessagePayload | null>(null);
  /**
   * Фон кадра приезжает контрактом игры, а не конфигурацией сессии: он
   * принадлежит игре, и при смене игры меняется вместе с ней.
   */
  const [background, setBackground] = useState<FrameBackground>(TRANSPARENT);
  /**
   * Числа для виджетов.
   *
   * Считаются ЗДЕСЬ, из того же потока событий, который и так приходит: сцена
   * их не знает, а отдельный пакет со счётчиками был бы вторым источником
   * правды о том же самом.
   */
  const [totals, setTotals] = useState<WidgetTotals>(EMPTY_TOTALS);
  /* Имена живут в состоянии, а не в рефе: их читает разметка виджетов, а
     значение, от которого зависит картинка, обязано вызывать перерисовку. */
  const [names, setNames] = useState<Record<string, string>>({});
  /**
   * Последняя команда звука с номером.
   *
   * Номер нужен потому, что два одинаковых нажатия одного пада - это ДВА
   * события: без него React увидел бы ту же ссылку и не перезапустил бы
   * звук.
   */
  const [sound, setSound] = useState<{ seq: number; payload: SoundCommand } | null>(null);
  /**
   * Была ли хоть одна команда сцены.
   *
   * Сами команды идут ШИНОЙ (`bus.pushStage`), а здесь только признак: он
   * решает, грузить ли ленивый чанк. Держать команды в состоянии корня
   * значило бы перерисовывать всё дерево на каждую гифку.
   */
  const [stageUsed, setStageUsed] = useState(false);
  /**
   * Шина каналов игры: создаётся один раз на токен и передаётся и соединению,
   * и сцене. Игра сокета не видит - см. realtime/bus.ts.
   */
  const bus = useMemo(() => createBus(), []);
  /**
   * Как сообщить серверу состояние сцены.
   *
   * Ставится соединением - так же, как отправка ресинхронизации и состава
   * элементов: сцена платформы сокета не видит.
   */
  const reportStage = useRef<((payload: StageStatePayload) => void) | null>(null);
  const sendStage = useCallback((payload: StageStatePayload) => {
    reportStage.current?.(payload);
  }, []);

  /** Итоговая громкость канала: микшер приезжает конфигурацией сессии. */
  const volumeOf = useCallback(
    (channel: SoundChannel) => {
      const mixer = config?.sound.mixer;

      if (mixer === undefined) {
        return 0.75;
      }

      return mixer.master.muted || mixer[channel].muted
        ? 0
        : mixer.master.volume * mixer[channel].volume;
    },
    [config],
  );

  const token = TOKEN;
  const debug = debugEnabled();

  useEffect(() => {
    if (token === null) {
      return;
    }

    const connection = connectOverlay(
      API_URL,
      token,
      {
        onStatus: setStatus,
        onSessionConfig: setConfig,
        onViewerEvent: (event: ViewerEvent) => {
          setCounters((current) => ({ ...current, events: current.events + 1 }));
          setTotals((current) => nextTotals(current, event));
        },
        onMembers: (members) => {
          setCounters((current) => ({
            ...current,
            members: current.members + Object.keys(members).length,
          }));

          /* Имена нужны спискам «кто больше дарит»: в потоке событий едет
             только идентификатор, имя приходит справочником один раз. */
          setNames((current) => {
            const next = { ...current };

            for (const [id, member] of Object.entries(members)) {
              next[id] = member.name;
            }

            return next;
          });
        },
        onAction: (command) => {
          setCounters((current) => ({ ...current, actions: current.actions + 1 }));
          setLastAction(`${command.actionId} x${command.magnitude} (${command.bindingKey})`);
        },
        onChatLine: () => {
          setCounters((current) => ({ ...current, chat: current.chat + 1 }));
        },
        onDirectorMessage: setDirectorMessage,
        onSound: (payload) => {
          setSound((current) => ({ seq: (current?.seq ?? 0) + 1, payload }));
        },
        onStage: (payload) => {
          setStageUsed(true);
          bus.pushStage(payload);
        },
        onStageReport: (report) => {
          reportStage.current = report;
        },
      },
      bus,
    );

    return () => {
      connection.close();
    };
  }, [token, bus]);

  /* Только подписка, без начального чтения: шина создаётся этим же
     компонентом, и контракта в ней до первого сообщения сокета быть не
     может - чтение здесь было бы синхронным setState в эффекте без повода. */
  useEffect(
    () =>
      bus.onContract((contract) => {
        setBackground(contract.frameBackground);
      }),
    [bus],
  );

  return (
    <OverlayFrame background={background}>
      {/*
        Сообщение зрителю об исчерпании квоты - единственное исключение из
        правила молчаливого overlay (блок 4а). Обоснование - QuotaNotice.tsx и
        docs/ARCHITECTURE.md, 4а.
      */}
      {config === null ? null : <QuotaNotice quota={config.quota} />}

      {/* Сообщение ведущего: платформа показывает его поверх сцены. */}
      <DirectorMessage message={directorMessage} />

      {/* Сцена лежит под watermark: watermark - метка платформы, она поверх. */}
      <SceneHost
        sceneId={config?.sceneId ?? null}
        locale={config?.locale ?? 'ru'}
        layout={config?.layout ?? EMPTY_LAYOUT}
        bus={bus}
      />

      {/*
        Виджеты - ПОВЕРХ сцены, но под watermark: метка платформы остаётся
        самой верхней. Порядок между самими виджетами и частями сцены правит
        стример через раскладку.
      */}
      {config !== null && config.widgetsEnabled && config.widgets.length > 0 && (
        <Suspense fallback={null}>
          <WidgetHost
            instances={config.widgets}
            layout={config.layout}
            totals={{
              ...totals,
              names,
              secondsLeft:
                config.quota.remainingMinutes === null ? null : config.quota.remainingMinutes * 60,
            }}
            bus={bus}
          />
        </Suspense>
      )}

      {/* Звук ничего не рисует: чанк подгружается первой командой. */}
      {sound !== null && (
        <Suspense fallback={null}>
          <SoundHost command={sound} mixer={config?.sound.mixer ?? defaultMixer()} />
        </Suspense>
      )}

      {/* Сцена платформы: пять подвижных частей поверх игры. */}
      {stageUsed && (
        <Suspense fallback={null}>
          <StageHost
            layout={config?.layout ?? EMPTY_LAYOUT}
            stream={config?.stream ?? NO_STREAM}
            volume={volumeOf}
            onState={sendStage}
            bus={bus}
          />
        </Suspense>
      )}

      {config?.plan.watermark === true ? <Watermark /> : null}

      {debug ? (
        <DebugBadge status={status} config={config} counters={counters} lastAction={lastAction} />
      ) : null}
    </OverlayFrame>
  );
}

/**
 * Диагностика для разработчика и для стримера с `?debug=1`.
 *
 * В эфире не появляется никогда: в production без явного параметра эта ветка
 * не рендерится вовсе.
 */
function DebugBadge({
  status,
  config,
  counters,
  lastAction,
}: {
  status: ConnectionStatus;
  config: SessionConfigPayload | null;
  counters: { events: number; members: number; actions: number; chat: number };
  lastAction: string | null;
}) {
  const text =
    status.kind === 'rejected'
      ? `отказ: ${status.reason.reason} - ${status.reason.message}`
      : status.kind;

  return (
    <div className="overlay-debug">
      <div>соединение: {text}</div>
      <div>
        конфигурация: {config === null ? 'не получена' : `тариф ${config.plan.id}`}
        {config !== null ? `, watermark ${config.plan.watermark ? 'да' : 'нет'}` : ''}
      </div>
      <div>сцена: {config?.sceneId ?? 'нет'}</div>
      <div>
        событий: {counters.events}, участников: {counters.members}
      </div>
      <div>
        команд: {counters.actions}, строк чата: {counters.chat}
      </div>
      <div>последняя команда: {lastAction ?? 'нет'}</div>
    </div>
  );
}

/**
 * Новые числа виджетов по пришедшему событию.
 *
 * Отдельной функцией, а не в обработчике: это чистое правило «что считается»,
 * и проверять его удобнее без сокета.
 */
function nextTotals(current: WidgetTotals, event: ViewerEvent): WidgetTotals {
  const id = event.actor.id;

  if (event.type === 'follow') {
    return { ...current, followers: current.followers + 1 };
  }

  if (event.type === 'share') {
    return { ...current, shares: current.shares + 1 };
  }

  if (event.type === 'like') {
    return {
      ...current,
      likes: current.likes + event.count,
      likers: { ...current.likers, [id]: (current.likers[id] ?? 0) + event.count },
    };
  }

  if (event.type === 'gift') {
    const coins = event.coins * event.count;

    return {
      ...current,
      coins: current.coins + coins,
      gifters: { ...current.gifters, [id]: (current.gifters[id] ?? 0) + coins },
    };
  }

  return current;
}
